import { ctx, hasRR, W } from '../canvas.js';
import { state } from '../state.js';
import { t } from '../i18n.js';
import { savedName } from '../progress.js';
import { LIFE_CAP } from '../config.js';

/** The scoreboard cards. Big enough to be read from the far end of a phone held sideways, which
 *  is the small end of what this game is played on: the canvas is 960 wide and a phone in
 *  landscape shows it at about two thirds, so sixteen pixels of writing arrived as eleven. */
const PANEL = { w: 285, h: 58, y: 16, pad: 18, r: 13, drop: 4, size: 20 };

/** The writing on the cards. A monospace bold face, because the scores are read as digits and
 *  a proportional one lets them wander while they change. */
const PANEL_FONT = `bold ${PANEL.size}px monospace`;

/** Where the writing goes on a card: inset from its edge, and on its own centre line.
 *
 *  Both are worked out from the card rather than written on the call. They were two numbers
 *  (32 and 45) until the card grew, at which point they were two numbers that had to be found:
 *  the baseline of a line of writing on a box is the box's centre plus about a third of the
 *  type, and that is a thing to say once. */
const TEXT_X = PANEL.pad + 14;
const TEXT_Y = PANEL.y + PANEL.h / 2 + Math.round(PANEL.size * 0.35);

/** The colour "it is your turn" is said in, here and on the clock in the header. */
const TURN_EDGE = '#ffd76a';
/** The edge of the panel that is not the one being played. */
const IDLE_EDGE = 'rgba(122, 142, 192, 0.45)';
/** The card's own colour, a shade under the sky it sits on. */
const CARD = '#0b1022';
/** The drop shadow of a card, and of the writing on it. */
const SHADE = 'rgba(2, 4, 10, 0.66)';

/** Panel `side`'s box. The left one is player 1 and the right one is player 2, because
 *  those are the sides the two of them are sitting on. */
function panelBox(side) {
  return { x: side === 0 ? PANEL.pad : W - PANEL.pad - PANEL.w, y: PANEL.y, w: PANEL.w, h: PANEL.h };
}

/**
 * The outline of a card. Rounded where the engine can do it and square where it cannot, which
 * is the same fork `rr` takes: `roundRect` is not in older Safari. The half pixel is what puts
 * a stroked edge on the pixel grid instead of across it — see the chat bubbles in banter.js,
 * which are drawn this way for the same reason.
 */
function cardPath(x, y, w, h, r, half) {
  const o = half ? 0.5 : 0;
  ctx.beginPath();
  if (hasRR) ctx.roundRect(x + o, y + o, w - o * 2, h - o * 2, r);
  else ctx.rect(x + o, y + o, w - o * 2, h - o * 2);
}

/**
 * One scoreboard: a card with a hard shadow under it, an edge, and the writing on top.
 *
 * It used to be two bare rectangles with no edge at all, which read as two holes cut in the
 * sky — and the only one with a border was the one whose turn it was, so half the scoreboard
 * looked unfinished and the other half looked like a box somebody had started drawing. Now
 * both are cards and only the lit one is lit: the shape says "scoreboard" and the gold says
 * whose turn it is, which are two different facts and were being said by the same thing.
 *
 * The shadow is offset down and not blurred, deliberately: it is the same 4px of hard shadow
 * the buttons in the bar wear, and a blur here would be a third kind of edge on a screen that
 * has two.
 */
function drawCard(side, lit) {
  const b = panelBox(side);

  ctx.fillStyle = SHADE;
  cardPath(b.x, b.y + PANEL.drop, b.w, b.h, PANEL.r, false);
  ctx.fill();

  ctx.fillStyle = CARD;
  cardPath(b.x, b.y, b.w, b.h, PANEL.r, false);
  ctx.fill();

  if (!lit) {
    ctx.strokeStyle = IDLE_EDGE;
    ctx.lineWidth = 2;
    cardPath(b.x, b.y, b.w, b.h, PANEL.r, true);
    ctx.stroke();
  }
}

/**
 * Whose turn it is, drawn on his own panel: the box's edge, lit, with a glow round it.
 *
 * The turn is already said twice — the clock in the header and the banner — and both of them
 * are words somewhere else on the screen. This is the one that
 * is where a player is already looking when he works out whether it is his go, and it costs a
 * stroke and a shadow.
 *
 * A breath rather than a lamp: it has to catch the eye when the turn arrives and then stop
 * asking for attention, because what follows is fifteen seconds of aiming.
 */
function drawTurnEdge(side) {
  const breath = 0.62 + 0.38 * Math.sin(state.clock / 420);
  const b = panelBox(side);

  ctx.save();
  ctx.globalAlpha = 0.5 + 0.5 * breath;
  ctx.strokeStyle = TURN_EDGE;
  ctx.lineWidth = 2;
  // A canvas glow is the shadow of the stroke, and there is no other way to ask for one.
  ctx.shadowColor = TURN_EDGE;
  ctx.shadowBlur = 5 + 13 * breath;
  cardPath(b.x - 1, b.y - 1, b.w + 2, b.h + 2, PANEL.r + 1, false);
  ctx.stroke();
  ctx.restore();
}

/** Writing with a pixel of shadow under it, which is what keeps it legible over a lit edge
 *  and is the cheapest half of the pixel-art look. */
function cardText(text, x, y, align) {
  ctx.save();
  ctx.textAlign = align;
  ctx.fillStyle = SHADE;
  ctx.fillText(text, x, y + 1);
  ctx.fillStyle = '#f3e9ce';
  ctx.fillText(text, x, y);
  ctx.restore();
}

/** La línea de abajo de la tarjeta, donde van los corazones. */
const LIFE_Y = PANEL.y + PANEL.h - 10;
/** De cuánto es un corazón, y cuánto se separan. */
const LIFE_S = 6;
const LIFE_GAP = 17;
/** El color de la vida extra. Un rojo de bomba de feria, y el único rojo del HUD. */
const LIFE_FILL = '#ff6b7a';

/**
 * Un corazón, dibujado en vez de escrito.
 *
 * No es un glifo a propósito: el marcador va en monoespaciada y un emoji de color en medio de esa
 * línea sale de otra tipografía —con su propio ancho y su propia altura—, que es justo lo que
 * este HUD evita en los números. Dos curvas y un pico miden siempre lo mismo y se pintan en el
 * color que se les diga.
 */
function heart(cx, cy, s) {
  ctx.beginPath();
  ctx.moveTo(cx, cy + s * 0.85);
  ctx.bezierCurveTo(cx - s * 1.15, cy - s * 0.1, cx - s * 0.55, cy - s, cx, cy - s * 0.35);
  ctx.bezierCurveTo(cx + s * 0.55, cy - s, cx + s * 1.15, cy - s * 0.1, cx, cy + s * 0.85);
  ctx.closePath();
  ctx.fill();
}

/**
 * Las vidas extra que le quedan a un lado, en el hueco de abajo de su tarjeta.
 *
 * Van una por corazón y no un número: el tope son dos, y dos cosas se cuentan de un vistazo
 * mientras que un "2" hay que leerlo. El lado que no tiene ninguna no pinta nada, que es lo que
 * hace que el corazón signifique algo cuando aparece.
 */
function drawLives(side) {
  const n = Math.max(0, Math.min(LIFE_CAP, state.lives[side] | 0));
  if (!n) return;

  ctx.save();
  for (let i = 0; i < n; i += 1) {
    // Los del lado propio crecen hacia dentro desde su esquina, y los del rival igual desde la
    // suya: el marcador está espejado, y una fila de corazones que creciera siempre hacia la
    // derecha se metería en el centro en la tarjeta de la derecha.
    const cx = side === 0
      ? TEXT_X + LIFE_S + i * LIFE_GAP
      : W - TEXT_X - LIFE_S - i * LIFE_GAP;
    const cy = LIFE_Y;
    ctx.fillStyle = SHADE;
    heart(cx, cy + 1, LIFE_S);
    ctx.fillStyle = LIFE_FILL;
    heart(cx, cy, LIFE_S);
  }
  ctx.restore();
}

// Score panels and the wind readout, drawn on top of the scene.
export function drawHud() {
  drawCard(0, !state.matchOver && state.turn === 0);
  drawCard(1, !state.matchOver && state.turn === 1);

  // Under the writing rather than over it, and not when the match is over because there is no
  // turn left to point at. A glow reaches a little past the edge it is drawn on, and the score
  // is not something to haze over.
  if (!state.matchOver) drawTurnEdge(state.turn);
  ctx.font = PANEL_FONT;
  ctx.textBaseline = 'alphabetic';
  // Mirrored, name outside: J1 - 0 on the left and 0 - J2 on the right. Each player's
  // name is on his own side of the board, which is the side he is sitting on, and the
  // two scores end up facing each other across the middle where a score belongs. It was
  // name-then-score on both sides, which put one of the two names on the wrong end.
  //
  // Y el nombre es el nick: quien lo escribe en la portada lo espera aquí, y esto ponía J1 y
  // nada más. J1/P1 solo cuando hay dos personas en el mismo teclado, que es cuando un nombre
  // no distingue a nadie (el campo es uno). En mayúsculas y recortado como el del rival: la
  // tarjeta mide 285 y a 20px de mono un nombre de dieciséis se come el marcador.
  const mine = String(savedName() || '').trim();
  const left = (state.cpuOn || state.online) && mine ? mine.slice(0, 14).toUpperCase() : t('hud.p1');
  cardText(left + ' - ' + state.score[0], TEXT_X, TEXT_Y, 'left');

  // The wind, in a card of its own: it is read as often as either score and it sits between
  // them, and as loose text the middle of the strip looked like the part nobody had got to.
  // Shorter than the scores and centred against them, so the strip reads as one row of cards
  // rather than three boxes with the middle one hanging from the ceiling.
  const wind = t('hud.wind') + '  ' + (state.wind > 0 ? '+' : '') + state.wind;
  const ww = Math.round(ctx.measureText(wind).width) + 36;
  const wh = PANEL.h - 14;
  const wy = PANEL.y + (PANEL.h - wh) / 2;
  ctx.fillStyle = SHADE;
  cardPath(W / 2 - ww / 2, wy + PANEL.drop, ww, wh, 11, false);
  ctx.fill();
  ctx.fillStyle = CARD;
  cardPath(W / 2 - ww / 2, wy, ww, wh, 11, false);
  ctx.fill();
  ctx.strokeStyle = IDLE_EDGE;
  ctx.lineWidth = 2;
  cardPath(W / 2 - ww / 2, wy, ww, wh, 11, true);
  ctx.stroke();
  cardText(wind, W / 2, TEXT_Y, 'center');

  // A rival over the wire gets their own name up there. The three letters of CPU would be
  // a lie, and P2 is what this scoreboard says about somebody in the same room. Recortado
  // como el nick propio: la tarjeta mide 285 y un nombre largo se come el marcador.
  const right = state.online
    ? String(state.rival || t('char.rival')).slice(0, 14).toUpperCase()
    : t(state.cpuOn ? 'hud.cpu' : 'hud.p2');
  cardText(state.score[1] + ' - ' + right, W - TEXT_X, TEXT_Y, 'right');

  // Y las vidas extra de cada uno, en el hueco de abajo de su tarjeta: es lo único que se gana
  // en una ronda y se gasta en la siguiente, así que tiene que estar a la vista sin que nadie
  // lo pida.
  drawLives(0);
  drawLives(1);
}
